import React, { useState, useEffect, useMemo } from 'react';
import './PowerPlantReport.css';
import { reportAPI, channelDataAPI } from '../../services/api';

const PowerPlantReport = ({ powerPlant, configData }) => {
  const [selectedEquipment, setSelectedEquipment] = useState({ type: 'integrated', data: { name: '통합' } });
  const [selectedQueryType, setSelectedQueryType] = useState('HOURLY');
  // 오늘 날짜를 YYYY-MM-DD 형식으로 가져오기
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(getTodayDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 설정 데이터에서 인버터 정보 가져오기 (API에서 가져온 데이터 사용)
  const inverters = useMemo(() => {
    return configData?.inverterConfigs?.map(config => ({
      id: config.id.toString(), // 인버터 설정 ID 사용 (inverterId가 아님)
      inverterId: config.inverterId,
      name: config.name
    })) || [];
  }, [configData?.inverterConfigs]);


  // 설정 데이터에서 접속반 정보 가져오기 (패널 API 데이터를 접속반으로 사용)
  const connectionBoxes = useMemo(() => {
    return configData?.panelConfigs?.map(config => ({
      id: config.id.toString(),
      panelId: config.panelId,
      name: config.name,
      status: 'normal' // 기본값
    })) || [];
  }, [configData?.panelConfigs]);
  
  // 설정 데이터에서 환경센서 정보 가져오기
  const environmentSensors = useMemo(() => {
    return configData?.temperatureConfig ? [{
      id: 'env-sensor-1',
      name: '환경센서'
    }] : [];
  }, [configData?.temperatureConfig]);




  // 실제 API를 호출하여 보고서 데이터 조회 (개별 인버터만)
  const fetchReportData = async () => {
    if (!powerPlant?.id) {
      setError('발전소 정보가 없습니다.');
      return;
    }

    // 개별 인버터가 선택되지 않은 경우 처리
    if (selectedEquipment.type !== 'inverter') {
      setError('개별 인버터를 선택해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiParams = {
        periodType: selectedQueryType,
        startDate: startDate,
        endDate: endDate
      };

      const id = selectedEquipment.data.id;

      console.log('보고서 API 호출:', { id, apiParams });
      const data = await reportAPI.getInverterReportData(id, apiParams);
      
      console.log('보고서 API 응답:', data);
      
      // 백엔드에서 받은 데이터를 테이블 형식으로 변환
      const formattedData = data.map(item => {
        // 백엔드에서 받은 날짜 데이터 (periodLabel 사용)
        let dateStr = item.periodLabel || item.periodStart || '';
        
        // periodLabel이 이미 포맷된 문자열이므로 그대로 사용
        // 필요시 추가 포맷팅
        if (dateStr && typeof dateStr === 'string') {
          try {
            // periodLabel이 "2025-10-01 00:00:00" 형식으로 이미 포맷되어 있으므로
            // 추가 변환 없이 그대로 사용하거나 필요시 포맷 변경
            if (selectedQueryType === 'HOURLY') {
              // 이미 "YYYY-MM-DD HH:mm:ss" 형식이므로 그대로 사용
              // dateStr = dateStr; // no-op
            } else if (selectedQueryType === 'DAILY') {
              // "YYYY-MM-DD" 부분만 추출
              dateStr = dateStr.split(' ')[0];
            } else if (selectedQueryType === 'MONTHLY') {
              // "YYYY-MM" 부분만 추출
              dateStr = dateStr.substring(0, 7);
            } else if (selectedQueryType === 'YEARLY') {
              // "YYYY" 부분만 추출
              dateStr = dateStr.substring(0, 4);
            }
          } catch (e) {
            console.error('날짜 파싱 오류:', e, dateStr);
          }
        }

        return {
          date: dateStr,
          generation: item.generatedPower ?? 0,
          cumulativeGeneration: item.cumulativeGeneratedPower ?? 0,
          totalCumulativeGeneration: item.totalGeneratedPower ?? 0
        };
      });

      console.log('포맷된 데이터:', formattedData);
      setReportData(formattedData);
    } catch (err) {
      console.error('보고서 데이터 조회 실패:', err);
      setError(err.response?.data?.message || '보고서 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 인버터 통합 보고서 데이터 조회 (모든 인버터 합산)
  const fetchIntegratedReportData = async () => {
    if (!powerPlant?.id) {
      setError('발전소 정보가 없습니다.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiParams = {
        periodType: selectedQueryType,
        startDate: startDate,
        endDate: endDate
      };

      console.log('통합 보고서 API 호출:', { powerLocationId: powerPlant.id, apiParams });
      const data = await reportAPI.getIntegratedReportData(powerPlant.id, apiParams);
      
      console.log('통합 보고서 API 응답:', data);
      
      // 백엔드에서 받은 데이터를 테이블 형식으로 변환
      const formattedData = data.map(item => {
        // 백엔드에서 받은 날짜 데이터 (periodLabel 사용)
        let dateStr = item.periodLabel || item.periodStart || '';
        
        // periodLabel이 이미 포맷된 문자열이므로 그대로 사용
        if (dateStr && typeof dateStr === 'string') {
          try {
            if (selectedQueryType === 'HOURLY') {
              // dateStr = dateStr; // no-op
            } else if (selectedQueryType === 'DAILY') {
              dateStr = dateStr.split(' ')[0];
            } else if (selectedQueryType === 'MONTHLY') {
              dateStr = dateStr.substring(0, 7);
            } else if (selectedQueryType === 'YEARLY') {
              dateStr = dateStr.substring(0, 4);
            }
          } catch (e) {
            console.error('날짜 파싱 오류:', e, dateStr);
          }
        }

        return {
          date: dateStr,
          generation: item.generatedPower ?? 0,
          cumulativeGeneration: item.cumulativeGeneratedPower ?? 0,
          totalCumulativeGeneration: item.totalGeneratedPower ?? 0
        };
      });

      console.log('통합 보고서 포맷된 데이터:', formattedData);
      setReportData(formattedData);
    } catch (err) {
      console.error('통합 보고서 데이터 조회 실패:', err);
      setError(err.response?.data?.message || '통합 보고서 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 환경센서 보고서 데이터 조회
  const fetchEnvironmentalSensorReport = async () => {
    if (!powerPlant?.id) {
      setError('발전소 정보가 없습니다.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiParams = {
        periodType: selectedQueryType,
        startDate: startDate,
        endDate: endDate
      };

      console.log('환경센서 보고서 API 호출:', { powerLocationId: powerPlant.id, apiParams });
      const data = await reportAPI.getEnvironmentalSensorReport(powerPlant.id, apiParams);
      
      console.log('환경센서 보고서 API 응답:', data);
      
      // 백엔드에서 받은 데이터를 프론트엔드 형식으로 변환
      const formattedData = data.map(item => {
        // dateTime을 문자열로 변환
        let dateStr = '';
        if (item.dateTime) {
          // LocalDateTime은 "2025-10-01T00:00:00" 형식으로 올 수 있음
          const dateTimeStr = typeof item.dateTime === 'string' ? item.dateTime : item.dateTime.toString();
          
          // 'T'를 공백으로 변경하여 표시
          dateStr = dateTimeStr.replace('T', ' ');
          
          // periodType에 따라 날짜 형식 조정
          if (selectedQueryType === 'DAILY') {
            dateStr = dateStr.split(' ')[0]; // "YYYY-MM-DD"
          } else if (selectedQueryType === 'MONTHLY') {
            dateStr = dateStr.substring(0, 7); // "YYYY-MM"
          } else if (selectedQueryType === 'YEARLY') {
            dateStr = dateStr.substring(0, 4); // "YYYY"
          }
        }

        return {
          date: dateStr,
          tiltedIrradiance: item.slopedRadiation ?? 0, // 경사일사량
          horizontalIrradiance: item.horizontalRadiation ?? 0, // 수평일사량
          outsideTemperature: item.ambientTemperature ?? 0, // 외기온도
          surfaceTemperature: item.surfaceTemperature ?? 0 // 표면온도
        };
      });

      console.log('환경센서 포맷된 데이터:', formattedData);
      setReportData(formattedData);
    } catch (err) {
      console.error('환경센서 보고서 데이터 조회 실패:', err);
      setError(err.response?.data?.message || '환경센서 보고서 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 채널(접속반) 보고서 데이터 조회
  const fetchChannelReport = async () => {
    if (!powerPlant?.id) {
      setError('발전소 정보가 없습니다.');
      return;
    }

    // 접속반이 선택되지 않은 경우 처리
    if (selectedEquipment.type !== 'connectionBox') {
      setError('접속반을 선택해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiParams = {
        periodType: selectedQueryType,
        startDate: startDate,
        endDate: endDate
      };

      const panelConfigId = selectedEquipment.data.id;

      console.log('채널 보고서 API 호출:', { panelConfigId, apiParams });
      const data = await channelDataAPI.getChannelReport(panelConfigId, apiParams);
      
      console.log('채널 보고서 API 응답:', data);
      
      // 백엔드에서 받은 데이터를 프론트엔드 형식으로 변환
      // 날짜별로 채널들을 세로로 나열하기 위한 구조로 변환
      const formattedData = [];
      
      data.forEach(item => {
        // dateTime을 문자열로 변환
        let dateStr = '';
        if (item.dateTime) {
          const dateTimeStr = typeof item.dateTime === 'string' ? item.dateTime : item.dateTime.toString();
          dateStr = dateTimeStr.replace('T', ' ');
          
          // periodType에 따라 날짜 형식 조정
          if (selectedQueryType === 'DAILY') {
            dateStr = dateStr.split(' ')[0];
          } else if (selectedQueryType === 'MONTHLY') {
            dateStr = dateStr.substring(0, 7);
          } else if (selectedQueryType === 'YEARLY') {
            dateStr = dateStr.substring(0, 4);
          }
        }

        // 각 채널별로 행을 생성하되, 날짜 정보를 함께 저장
        if (item.channelDataList && item.channelDataList.length > 0) {
          item.channelDataList.forEach((channelData, index) => {
            formattedData.push({
              date: dateStr,
              isFirstChannel: index === 0, // 첫 번째 채널인지 확인
              channelCount: item.channelDataList.length, // rowspan용
              channel: `ch-${channelData.channelNumber}`,
              voltage: channelData.voltage ?? 0,
              current: channelData.current ?? 0
            });
          });
        }
      });

      console.log('채널 보고서 포맷된 데이터:', formattedData);
      setReportData(formattedData);
    } catch (err) {
      console.error('채널 보고서 데이터 조회 실패:', err);
      setError(err.response?.data?.message || '채널 보고서 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 조회 버튼 클릭
  const handleQuery = () => {
    if (selectedEquipment.type === 'inverter') {
      // 개별 인버터 실제 API 호출
      fetchReportData();
    } else if (selectedEquipment.type === 'integrated') {
      // 통합 인버터 실제 API 호출
      fetchIntegratedReportData();
    } else if (selectedEquipment.type === 'connectionBox') {
      // 접속반(채널) 실제 API 호출
      fetchChannelReport();
    } else if (selectedEquipment.type === 'environmentSensor') {
      // 환경센서 실제 API 호출
      fetchEnvironmentalSensorReport();
    }
  };

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    if (!reportData) return;
    
    let headers = [];
    let csvContent = '';
    
    if (selectedEquipment.type === 'integrated' || selectedEquipment.type === 'inverter') {
      headers = ['날짜', '발전량', '누적발전량', '총 누적발전량'];
      csvContent = [
        headers.join(','),
        ...reportData.map(row => [
          row.date,
          row.generation,
          row.cumulativeGeneration,
          row.totalCumulativeGeneration
        ].join(','))
      ].join('\n');
    } else if (selectedEquipment.type === 'connectionBox') {
      headers = ['날짜', '채널', '전압', '전류'];
      csvContent = [
        headers.join(','),
        ...reportData.map(row => [
          row.date,
          row.channel,
          row.voltage,
          row.current
        ].join(','))
      ].join('\n');
    } else if (selectedEquipment.type === 'environmentSensor') {
      headers = ['날짜', '경사일사량(Wh/m²)', '수평일사량(Wh/m²)', '외기온도(℃)', '표면온도(℃)'];
      csvContent = [
        headers.join(','),
        ...reportData.map(row => [
          row.date,
          row.tiltedIrradiance,
          row.horizontalIrradiance,
          row.outsideTemperature,
          row.surfaceTemperature
        ].join(','))
      ].join('\n');
    }
    
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `report_${selectedQueryType}_${startDate}_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 초기 로드 시에는 아무것도 하지 않음 (장비 선택 후에만 데이터 로드)
  useEffect(() => {
    // 초기 로드는 제거 - 사용자가 장비를 선택한 후에만 데이터 로드
  }, []);

  // 인버터(통합/개별), 접속반, 환경센서 선택 시 자동으로 데이터 조회
  useEffect(() => {
    if (selectedEquipment?.type === 'integrated') {
      // 통합 인버터가 선택되면 자동으로 데이터 조회
      fetchIntegratedReportData();
    } else if (selectedEquipment?.type === 'inverter') {
      // 개별 인버터가 선택되면 자동으로 데이터 조회
      fetchReportData();
    } else if (selectedEquipment?.type === 'connectionBox') {
      // 접속반이 선택되면 자동으로 데이터 조회
      fetchChannelReport();
    } else if (selectedEquipment?.type === 'environmentSensor') {
      // 환경센서가 선택되면 자동으로 데이터 조회
      fetchEnvironmentalSensorReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEquipment?.type, selectedEquipment?.data?.id]);

  // 드롭다운용 전체 장비 목록 생성
  const allEquipmentOptions = useMemo(() => {
    const options = [];
    // 통합
    options.push({
      value: 'integrated',
      label: '[인버터] 통합',
      type: 'integrated',
      data: { name: '통합' }
    });
    // 인버터
    inverters.forEach(inverter => {
      options.push({
        value: `inverter-${inverter.id}`,
        label: `[인버터] ${inverter.name} - ${inverter.inverterId}`,
        type: 'inverter',
        data: inverter,
        id: inverter.id
      });
    });
    // 접속반
    connectionBoxes.forEach(box => {
      options.push({
        value: `connectionBox-${box.id}`,
        label: `[접속반] ${box.name} - ${box.panelId}`,
        type: 'connectionBox',
        data: box,
        id: box.id
      });
    });
    // 환경센서
    environmentSensors.forEach(sensor => {
      options.push({
        value: `environmentSensor-${sensor.id}`,
        label: `[환경센서] ${sensor.name}`,
        type: 'environmentSensor',
        data: sensor,
        id: sensor.id
      });
    });
    return options;
  }, [inverters, connectionBoxes, environmentSensors]);

  // 드롭다운 선택 값
  const dropdownValue = useMemo(() => {
    if (!selectedEquipment.type) return '';
    if (selectedEquipment.type === 'integrated') return 'integrated';
    const id = selectedEquipment.id;
    return `${selectedEquipment.type}-${id}`;
  }, [selectedEquipment]);

  // 드롭다운 변경 핸들러
  const handleDropdownChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    
    const option = allEquipmentOptions.find(opt => opt.value === value);
    if (!option) return;

    setSelectedEquipment({ 
      type: option.type, 
      data: option.data,
      id: option.id 
    });
  };

  return (
    <div className="power-plant-report">
      {/* 모바일용 장비 선택 드롭다운 */}
      <div className="mobile-equipment-select">
        <select 
          value={dropdownValue}
          onChange={handleDropdownChange}
          className="equipment-dropdown"
        >
          <option value="">장비를 선택하세요</option>
          {allEquipmentOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="report-layout">
        {/* 왼쪽 패널 - 트렌드와 동일한 구조 */}
        <div className="left-panel">
          {/* 인버터 섹션 */}
          <div className="equipment-section">
            <h3>인버터</h3>
            <div 
              className={`trend-equipment-item ${selectedEquipment.type === 'integrated' ? 'selected' : ''}`}
              onClick={() => setSelectedEquipment({ type: 'integrated', data: { name: '통합' } })}
            >
              통합
            </div>
            {inverters.map((inverter) => (
              <div 
                key={inverter.id} 
                className={`trend-equipment-item ${selectedEquipment?.id === inverter.id ? 'selected' : ''}`}
                onClick={() => setSelectedEquipment({ type: 'inverter', data: inverter, id: inverter.id })}
              >
                {inverter.name} - {inverter.inverterId}
              </div>
            ))}
          </div>

          {/* 접속반 섹션 */}
          {connectionBoxes.length > 0 && (
          <div className="equipment-section">
            <h3>접속반</h3>
            {connectionBoxes.map((box) => (
              <div 
                key={box.id} 
                className={`trend-equipment-item ${selectedEquipment?.id === box.id ? 'selected' : ''}`}
                onClick={() => setSelectedEquipment({ type: 'connectionBox', data: box, id: box.id })}
              >
                {box.name} - {box.panelId}
              </div>
            ))}
          </div>
          )}

          {/* 환경센서 섹션 */}
          {environmentSensors.length > 0 && (
          <div className="equipment-section">
            <h3>환경센서</h3>
            {environmentSensors.map((sensor) => (
              <div 
                key={sensor.id} 
                className={`trend-equipment-item ${selectedEquipment?.id === sensor.id ? 'selected' : ''}`}
                onClick={() => setSelectedEquipment({ type: 'environmentSensor', data: sensor, id: sensor.id })}
              >
                {sensor.name}
              </div>
            ))}
          </div>
          )}
        </div>

        {/* 중앙 패널 - 보고서 내용 */}
        <div className="right-panel">
          {selectedEquipment.type ? (
            <>
              {/* 조회 조건 */}
              <div className="query-controls">
                <div className="query-left-section">
                  <div className="query-section">
                    <h4>조회기간 설정</h4>
                    <div className="date-inputs">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                      <span>-</span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="query-section">
                    <h4>조회종류</h4>
                    <div className="query-type-buttons">
                      <button 
                        className={selectedQueryType === 'HOURLY' ? 'active' : ''}
                        onClick={() => setSelectedQueryType('HOURLY')}
                      >
                        시간
                      </button>
                      <button 
                        className={selectedQueryType === 'DAILY' ? 'active' : ''}
                        onClick={() => setSelectedQueryType('DAILY')}
                      >
                        일간
                      </button>
                      <button 
                        className={selectedQueryType === 'MONTHLY' ? 'active' : ''}
                        onClick={() => setSelectedQueryType('MONTHLY')}
                      >
                        월간
                      </button>
                      <button 
                        className={selectedQueryType === 'YEARLY' ? 'active' : ''}
                        onClick={() => setSelectedQueryType('YEARLY')}
                      >
                        연간
                      </button>
                    </div>
                  </div>

                  <button 
                    className="query-button" 
                    onClick={handleQuery}
                    disabled={loading}
                  >
                    {loading ? '조회 중...' : '조회'}
                  </button>
                </div>

                <button className="excel-button" onClick={handleExcelDownload}>
                  📊 엑셀다운로드
                </button>
              </div>

              {/* 보고서 테이블 */}
              <div className="report-table-container">
                <h4>
                  {selectedEquipment.type === 'integrated' ? '인버터 > 통합' : 
                   selectedEquipment.type === 'inverter' ? `인버터 > ${selectedEquipment.data.name}` :
                   selectedEquipment.type === 'connectionBox' ? `접속반 > ${selectedEquipment.data.name}` :
                   `환경센서 > ${selectedEquipment.data.name}`}
                </h4>

                {/* 에러 메시지 */}
                {error && (
                  <div className="error-message" style={{ 
                    color: '#dc2626', 
                    backgroundColor: '#fef2f2', 
                    padding: '12px', 
                    borderRadius: '6px', 
                    marginBottom: '16px',
                    border: '1px solid #fecaca'
                  }}>
                    {error}
                  </div>
                )}
                
                {loading && (
                  <div className="loading-message" style={{ 
                    textAlign: 'center', 
                    padding: '20px', 
                    color: '#6b7280' 
                  }}>
                    데이터를 불러오는 중...
                  </div>
                )}
                
                {reportData && !loading && (
                  <div className="report-table-wrapper">
                    <table className="report-table">
                      <thead>
                        <tr>
                          {selectedEquipment.type === 'connectionBox' ? (
                            <>
                              <th>날짜</th>
                              <th>채널</th>
                              <th>전압</th>
                              <th>전류</th>
                            </>
                          ) : selectedEquipment.type === 'environmentSensor' ? (
                            <>
                              <th>날짜</th>
                              <th>경사일사량(Wh/m²)</th>
                              <th>수평일사량(Wh/m²)</th>
                              <th>외기온도(℃)</th>
                              <th>표면온도(℃)</th>
                            </>
                          ) : (
                            <>
                              <th>날짜</th>
                              <th>발전량</th>
                              <th>누적발전량</th>
                              <th>총 누적발전량</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.map((row, index) => (
                          <tr key={index}>
                            {selectedEquipment.type === 'connectionBox' ? (
                              <>
                                {row.isFirstChannel && (
                                  <td rowSpan={row.channelCount}>{row.date}</td>
                                )}
                                <td>{row.channel}</td>
                                <td>{row.voltage}</td>
                                <td>{row.current}</td>
                              </>
                            ) : selectedEquipment.type === 'environmentSensor' ? (
                              <>
                                <td>{row.date}</td>
                                <td>{row.tiltedIrradiance}</td>
                                <td>{row.horizontalIrradiance}</td>
                                <td>{row.outsideTemperature}</td>
                                <td>{row.surfaceTemperature}</td>
                              </>
                            ) : (
                              <>
                                <td>{row.date}</td>
                                <td>{row.generation}</td>
                                <td>{row.cumulativeGeneration}</td>
                                <td>{row.totalCumulativeGeneration}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {reportData && reportData.length === 0 && !loading && (
                  <div className="no-data-message" style={{ 
                    textAlign: 'center', 
                    padding: '40px', 
                    color: '#6b7280',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    조회된 데이터가 없습니다.
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>왼쪽에서 장비를 선택해주세요</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PowerPlantReport;
